import React, { useState } from "react";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RichField, SelectField, NumField } from "../fields";
import { Plus, Trash2, Sparkles, Copy as CopyIcon, ArrowUp, ArrowDown, ChevronDown, ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ResultMappingEditor, LayoutTogglesEditor, ResultKeyResolverEditor } from "./result_engine";
import { learnMoreLists } from "@/pages/resultPresentation";

// Must match FREE_LICENSE_OVERRIDE_PHRASE in backend/server.py.
export const FREE_LICENSE_PHRASE = "AKTIFKAN BERBAYAR";

// Asks the admin to type the phrase before paid mode is turned on for a free-licensed instrument.
function FreeLicenseDialog({ note, lang, onConfirm, onClose }) {
  const [phrase, setPhrase] = useState("");
  const id = lang === "id";
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive"><ShieldAlert className="w-5 h-5" />{id ? "Instrumen ini bukan Mindovia Original" : "This instrument is not a Mindovia Original"}</DialogTitle>
          <DialogDescription>
            {id
              ? "Instrumen ini berlisensi gratis atau memakai skala terbit milik orang lain. Mode berbayar menyembunyikan skor rinci sampai responden membayar, dan itu bisa melanggar izin pemakaiannya."
              : "This instrument has a free-use licence or uses someone else's published scale. Paid mode hides detailed scores until the respondent pays, which may break the terms under which it is used."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {note && <p className="rounded-lg border border-border bg-muted/40 p-3 whitespace-pre-line" data-testid="free-license-dialog-note">{note}</p>}
          <p className="text-muted-foreground">{id ? "Ketik" : "Type"} <span className="font-mono text-foreground">{FREE_LICENSE_PHRASE}</span> {id ? "untuk melanjutkan." : "to continue."}</p>
          <Input value={phrase} onChange={(e) => setPhrase(e.target.value)} data-testid="free-license-confirm-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{id ? "Batal" : "Cancel"}</Button>
          <Button className="bg-destructive text-white" disabled={phrase.trim() !== FREE_LICENSE_PHRASE} onClick={() => onConfirm(phrase.trim())} data-testid="free-license-confirm-submit">
            {id ? "Aktifkan berbayar" : "Turn on paid mode"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const PLACEHOLDERS = [
  "{respondent_name}", "{instrument_name}", "{date}",
  "{primary_dimension}", "{secondary_dimension}",
  "{score}", "{result_profile}",
];

function PlaceholderChips({ onInsert }) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {PLACEHOLDERS.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onInsert(p)}
          className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-sage-100 text-teal-800 border border-sage-200 hover:bg-sage-200"
          data-testid={`ph-chip-${p.replace(/[{}]/g, "")}`}
          title={`Insert ${p}`}
        >{p}</button>
      ))}
    </div>
  );
}

function ResultGroup({ id, title, hint, open, children }) {
  return (
    <details open={open} className="group rounded-2xl border border-border bg-card/60" data-testid={`result-group-${id}`}>
      <summary className="flex items-center justify-between gap-3 p-5 cursor-pointer list-none select-none">
        <div>
          <div className="font-display text-xl">{title}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>
        </div>
        <ChevronDown className="w-5 h-5 text-muted-foreground shrink-0 transition-transform group-open:rotate-180" />
      </summary>
      <div className="px-5 pb-5 space-y-6">{children}</div>
    </details>
  );
}

const ORIGIN_OPTIONS = [
  { value: "original", id: "Mindovia Original", en: "Mindovia Original" },
  { value: "published", id: "Skala terbit", en: "Published scale" },
  { value: "", id: "Belum ditandai", en: "Not set" },
];

// Same rule as _infer_license_origin in backend/server.py: the explicit choice, else the
// "Jenis:"/"Type:" line of the source note.
export function licenseOrigin(license) {
  if (license.origin === "original" || license.origin === "published") return license.origin;
  for (const line of String(license.note || "").split("\n")) {
    const [head, ...rest] = line.split(":");
    if (!["jenis", "type"].includes(head.trim().toLowerCase())) continue;
    const value = rest.join(":").toLowerCase();
    if (value.includes("mindovia original")) {
      if (!value.includes("/")) return "original";
      continue;
    }
    if (["terbit", "adaptasi", "published", "adapted"].some((w) => value.includes(w))) return "published";
  }
  return license.free_only ? "published" : "";
}

const LICENSE_NOTE_TEMPLATE = {
  id: "Jenis: Mindovia Original / Adaptasi instrumen terbit / Instrumen terbit (item asli)\nPenyusun dan sitasi: \nLisensi: Domain publik / CC BY 4.0 / CC BY-NC-SA 4.0 / Izin nonkomersial dari penyusun / Berbayar\nBukti: link halaman lisensi atau email izin\nTanggal dicek: ",
  en: "Type: Mindovia Original / Adapted published instrument / Published instrument (original items)\nAuthors and citation: \nLicence: Public domain / CC BY 4.0 / CC BY-NC-SA 4.0 / Non-commercial permission from authors / Paid\nEvidence: licence page link or permission email\nDate checked: ",
};

export default function ResultTab({ inst, set, lang, instrumentId }) {
  const chart = inst.chart_config || {};
  const setChart = (patch) => set("chart_config", { ...chart, ...patch });
  const share = inst.share_config || {};
  const setShare = (patch) => set("share_config", { ...share, ...patch });
  const pdf = inst.pdf_config || {};
  const setPdf = (patch) => set("pdf_config", { ...pdf, ...patch });
  const license = inst.license_config || {};
  const setLicense = (patch) => set("license_config", { ...license, ...patch });
  const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);
  const setPaidMode = (on) => {
    if (on && (license.free_only || licenseOrigin(license) === "published")) setLicenseDialogOpen(true);
    else setPdf({ mode: on ? "paid" : "free" });
  };
  const ai = inst.ai_config || {};
  const setAi = (patch) => set("ai_config", { ...ai, ...patch });

  const rt = inst.result_template || {};
  const setRt = (patch) => set("result_template", { ...rt, ...patch });
  const sections = rt.report_sections || [];
  const setSections = (arr) => setRt({ report_sections: arr });
  const addSection = () => setSections([...sections, { key: `sec_${sections.length + 1}`, title_id: "", title_en: "", body_id: "", body_en: "", order: sections.length + 1 }]);
  const updSection = (i, patch) => setSections(sections.map((s, k) => k === i ? { ...s, ...patch } : s));

  // Hero card display config (badge + intro)
  const rd = inst.result_display_config || {};
  const setRd = (patch) => set("result_display_config", { ...rd, ...patch });
  const rmSection = (i) => setSections(sections.filter((_, k) => k !== i));
  const moveSection = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= sections.length) return;
    const arr = [...sections];[arr[i], arr[j]] = [arr[j], arr[i]];
    setSections(arr.map((s, k) => ({ ...s, order: k + 1 })));
  };

  const [previewText, setPreviewText] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const previewAi = async () => {
    if (!instrumentId) { toast.error(lang === "id" ? "Simpan instrumen dulu untuk pratinjau AI." : "Save first to preview AI."); return; }
    setPreviewing(true);
    try {
      const { data } = await api.post(`/admin/instruments/${instrumentId}/ai-narrative/preview?lang=${lang}`);
      setPreviewText(data.text || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setPreviewing(false); }
  };

  return (
    <div className="space-y-6" data-wrap="responsive">
      <ResultGroup id="result" open={true} title={lang === "id" ? "Hasil dan character card" : "Results and character cards"} hint={lang === "id" ? "Urutannya: cara sistem memilih key hasil, lalu character card untuk tiap key. Tingkat untuk tes skor total diatur di tab Scoring." : "In order: how the result key is chosen, then a character card for each key. Levels for total-score tests are set in the Scoring tab."}>
        <ResultKeyResolverEditor
          resolver={inst.result_key_resolver || { mode: "generic", top_n: 3, combined_key_dim_id: null, combined_bands: [] }}
          onChange={(r) => set("result_key_resolver", r)}
          method={(inst.scoring || {}).method || "continuous_multi"}
          combinedDims={(inst.scoring || {}).combined_dimensions || []}
          dimensions={inst.dimensions || []}
          criteria={(inst.scoring || {}).criteria || []}
          resultMapping={inst.result_mapping || []}
          lang={lang}
        />

        <ResultMappingEditor
          mapping={inst.result_mapping || []}
          onChange={(arr) => set("result_mapping", arr)}
          dimensions={inst.dimensions || []}
          method={(inst.scoring || {}).method || "continuous_multi"}
          combinedDims={(inst.scoring || {}).combined_dimensions || []}
          severityLevels={inst.severity_levels || []}
          resolver={inst.result_key_resolver || {}}
          lists={learnMoreLists(inst)}
          onListsChange={(l) => set("learn_more_lists", l)}
          lang={lang}
        />
      </ResultGroup>

      <ResultGroup id="page" open={false} title={lang === "id" ? "Tampilan halaman hasil" : "Result page display"} hint={lang === "id" ? "Bagian yang tampil, grafik, teks pembuka dan penutup, serta bagian tambahan." : "Visible sections, chart, intro and footer text, and extra sections."}>
        {/* Subtitle under the "Profil Kamu" heading on the public result page */}
        <div className="p-6 rounded-2xl border border-border bg-card space-y-4" data-testid="rt-hero-block">
          <div>
            <div className="font-display text-lg">{lang === "id" ? "Teks di Bawah Judul Hasil" : "Text Under the Result Heading"}</div>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
              {lang === "id"
                ? "Satu kalimat di bawah judul \"Profil Kamu\". Kosongkan untuk memakai teks bawaan."
                : "One sentence under the \"Your Profile\" heading. Leave empty for the default text."}
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Indonesia</Label>
              <Textarea rows={2} value={rd.intro_text_id || ""} onChange={(e) => setRd({ intro_text_id: e.target.value })} placeholder={lang === "id" ? "Tinjau hasil kamu dan pelajari lebih dalam tentang dirimu." : "Review your result and learn more about yourself."} data-testid="rd-intro-id" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">English</Label>
              <Textarea rows={2} value={rd.intro_text_en || ""} onChange={(e) => setRd({ intro_text_en: e.target.value })} placeholder="Review your result and learn more about yourself." data-testid="rd-intro-en" />
            </div>
          </div>
        </div>

        <LayoutTogglesEditor
          layout={inst.result_layout || { show_hero: true, show_graph: true, show_ranking: false, show_dimensions: true, show_supporting: false, show_reflection: true, show_combined: false }}
          onChange={(l) => set("result_layout", l)}
          lang={lang}
        />
        {/* Display toggles */}
        <div className="p-6 rounded-2xl border border-border bg-card space-y-4">
          <div className="font-display text-lg">{lang === "id" ? "Tampilan Hasil" : "Result Display"}</div>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm"><Switch checked={rt.show_total} onCheckedChange={(v) => setRt({ show_total: v })} data-testid="rt-show-total" /> Show total score</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!rt.show_respondent_info} onCheckedChange={(v) => setRt({ show_respondent_info: v })} data-testid="rt-show-respondent" /> {lang === "id" ? "Tampilkan info responden (hanya di PDF berbayar)" : "Show respondent info (paid PDF only)"}</label>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><RichField label="Intro (ID)" value={rt.intro_id} onChange={(v) => setRt({ intro_id: v })} testid="rt-intro-id" /><PlaceholderChips onInsert={(p) => setRt({ intro_id: (rt.intro_id || "") + " " + p })} /></div>
            <div><RichField label="Intro (EN)" value={rt.intro_en} onChange={(v) => setRt({ intro_en: v })} testid="rt-intro-en" /><PlaceholderChips onInsert={(p) => setRt({ intro_en: (rt.intro_en || "") + " " + p })} /></div>
            <div><RichField label="Footer (ID)" value={rt.footer_id} onChange={(v) => setRt({ footer_id: v })} testid="rt-footer-id" /><PlaceholderChips onInsert={(p) => setRt({ footer_id: (rt.footer_id || "") + " " + p })} /></div>
            <div><RichField label="Footer (EN)" value={rt.footer_en} onChange={(v) => setRt({ footer_en: v })} testid="rt-footer-en" /><PlaceholderChips onInsert={(p) => setRt({ footer_en: (rt.footer_en || "") + " " + p })} /></div>
          </div>
        </div>

        {/* Chart config */}
        <div className="p-6 rounded-2xl border border-border bg-card space-y-4">
          <div className="font-display text-lg">{lang === "id" ? "Grafik Hasil" : "Result Chart"}</div>
          <div className="grid md:grid-cols-3 gap-4">
            <SelectField label={lang === "id" ? "Tipe grafik" : "Chart type"} testid="chart-type" value={chart.type || "bar_horizontal"} onChange={(v) => setChart({ type: v })}
              options={[{ value: "bar_horizontal", label: "Bar Horizontal" }, { value: "bar_vertical", label: "Bar Vertikal" }, { value: "radar", label: "Radar" }, { value: "pie", label: "Pie" }, { value: "donut", label: "Donut" }]} />
            <NumField label={lang === "id" ? "Top N" : "Top N"} testid="chart-topn" value={chart.top_n ?? 3} onChange={(v) => setChart({ top_n: v })} />
            <div className="flex items-end gap-2 pt-6 flex-wrap">
              <label className="flex items-center gap-2 text-sm"><Switch checked={chart.show_percentage !== false} onCheckedChange={(v) => setChart({ show_percentage: v })} data-testid="chart-show-pct" /> %</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={!!chart.show_top_only} onCheckedChange={(v) => setChart({ show_top_only: v })} data-testid="chart-show-top" /> Top only</label>
            </div>
          </div>
        </div>

        {/* Report Sections (Fase 3 — editable templates) */}
        <div className="p-6 rounded-2xl border border-border bg-card space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display text-lg">{lang === "id" ? "Bagian Laporan Kustom" : "Custom Report Sections"}</div>
              <div className="text-xs text-muted-foreground">{lang === "id" ? "Bagian tambahan yang muncul di halaman hasil dan PDF." : "Extra sections shown on the result page and PDF."}</div>
            </div>
            <Button size="sm" variant="outline" onClick={addSection} data-testid="rs-add-btn"><Plus className="w-3 h-3 mr-1" /> {lang === "id" ? "Tambah Bagian" : "Add Section"}</Button>
          </div>
          {sections.map((sec, i) => (
            <div key={i} className="p-4 rounded-xl border border-border bg-secondary/40 space-y-3" data-testid={`rs-row-${i}`}>
              <div className="flex items-center gap-2">
                <Input value={sec.key} onChange={(e) => updSection(i, { key: e.target.value })} className="h-8 max-w-xs font-mono text-xs" placeholder="section-key" data-testid={`rs-key-${i}`} />
                <div className="ml-auto flex items-center gap-1">
                  <Button size="icon" variant="ghost" onClick={() => moveSection(i, -1)} disabled={i === 0}><ArrowUp className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => moveSection(i, 1)} disabled={i === sections.length - 1}><ArrowDown className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => rmSection(i)} className="text-destructive" data-testid={`rs-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Title (ID)</Label><Input value={sec.title_id} onChange={(e) => updSection(i, { title_id: e.target.value })} data-testid={`rs-title-id-${i}`} /></div>
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Title (EN)</Label><Input value={sec.title_en} onChange={(e) => updSection(i, { title_en: e.target.value })} data-testid={`rs-title-en-${i}`} /></div>
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Body (ID)</Label><Textarea rows={3} value={sec.body_id} onChange={(e) => updSection(i, { body_id: e.target.value })} data-testid={`rs-body-id-${i}`} /><PlaceholderChips onInsert={(p) => updSection(i, { body_id: (sec.body_id || "") + " " + p })} /></div>
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Body (EN)</Label><Textarea rows={3} value={sec.body_en} onChange={(e) => updSection(i, { body_en: e.target.value })} data-testid={`rs-body-en-${i}`} /><PlaceholderChips onInsert={(p) => updSection(i, { body_en: (sec.body_en || "") + " " + p })} /></div>
              </div>
            </div>
          ))}
        </div>

      </ResultGroup>

      <ResultGroup id="share" open={false} title={lang === "id" ? "Berbagi" : "Sharing"} hint={lang === "id" ? "Tautan publik dan teks pada gambar share." : "Public link and the text on share images."}>
        {/* Sharing / Social */}
        <div className="p-6 rounded-2xl border border-border bg-card space-y-4">
          <div className="font-display text-lg">{lang === "id" ? "Berbagi & Kartu Sosial" : "Sharing & Social Card"}</div>
          <div className="grid md:grid-cols-3 gap-4">
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!share.public_share_enabled} onCheckedChange={(v) => setShare({ public_share_enabled: v })} data-testid="share-public-toggle" /> {lang === "id" ? "Aktifkan tautan publik (tanpa PII)" : "Enable public share link (no PII)"}</label>
            <SelectField label={lang === "id" ? "Tampilkan identitas" : "Identity display"} testid="share-identity" value={share.identity_display || "none"} onChange={(v) => setShare({ identity_display: v })}
              options={[{ value: "none", label: lang === "id" ? "Tidak ada" : "None" }, { value: "initials", label: "Initials (L.M.)" }, { value: "first_name", label: lang === "id" ? "Nama depan" : "First name" }]} />
          </div>
          <div className="pt-4 border-t border-border space-y-3" data-testid="share-text-block">
            <div>
              <div className="font-display text-base">{lang === "id" ? "Teks Tambahan Gambar Share" : "Share Image Text"}</div>
              <p className="text-xs text-muted-foreground max-w-2xl">
                {lang === "id"
                  ? "Tampil di atas dan di bawah kartu pada gambar Story dan Persegi. Gambar Feed hanya berisi kartu. Bisa memakai {code} (kode Holland), {title} (judul hasil), {test} (nama tes), dan {website}. Kosongkan untuk memakai teks bawaan."
                  : "Shown above and below the card on Story and Square images. Feed images show only the card. You can use {code} (Holland code), {title} (result title), {test} (test name) and {website}. Leave empty for the default text."}
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {[
                ["share_hook_id", lang === "id" ? "Teks atas (ID)" : "Top line (ID)", "Ini hasilku!"],
                ["share_hook_en", lang === "id" ? "Teks atas (EN)" : "Top line (EN)", "Here's my result!"],
                ["share_cta_id", lang === "id" ? "Ajakan bawah (ID)" : "Bottom call to action (ID)", "Penasaran hasilmu? Coba di {website}"],
                ["share_cta_en", lang === "id" ? "Ajakan bawah (EN)" : "Bottom call to action (EN)", "Curious about yours? Try it at {website}"],
              ].map(([key, label, placeholder]) => (
                <div key={key}>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
                  <Input value={share[key] || ""} placeholder={placeholder} maxLength={90} onChange={(e) => setShare({ [key]: e.target.value })} data-testid={`share-text-${key}`} />
                </div>
              ))}
            </div>
          </div>
        </div>

      </ResultGroup>

      <ResultGroup id="pdf" open={false} title={lang === "id" ? "PDF dan Narasi AI" : "PDF and AI narrative"} hint={lang === "id" ? "Laporan PDF, monetisasi, dan narasi AI reflektif." : "PDF report, monetization and the reflective AI narrative."}>
        {/* PDF settings */}
        <div className="p-6 rounded-2xl border border-border bg-card space-y-4">
          <div className="font-display text-lg">{lang === "id" ? "Laporan PDF" : "PDF Report"}</div>
          <div className="grid md:grid-cols-3 gap-3">
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Brand name</Label><Input value={pdf.brand_name || ""} onChange={(e) => setPdf({ brand_name: e.target.value })} data-testid="pdf-brand" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Tagline</Label><Input value={pdf.tagline || ""} onChange={(e) => setPdf({ tagline: e.target.value })} data-testid="pdf-tagline" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Contact email</Label><Input value={pdf.contact_email || ""} onChange={(e) => setPdf({ contact_email: e.target.value })} data-testid="pdf-contact" /></div>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!pdf.show_respondent_info} onCheckedChange={(v) => setPdf({ show_respondent_info: v })} data-testid="pdf-show-resp" /> {lang === "id" ? "Info responden" : "Respondent info"}</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={pdf.show_chart !== false} onCheckedChange={(v) => setPdf({ show_chart: v })} data-testid="pdf-show-chart" /> {lang === "id" ? "Grafik" : "Chart"}</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={pdf.show_interpretation !== false} onCheckedChange={(v) => setPdf({ show_interpretation: v })} data-testid="pdf-show-interp" /> {lang === "id" ? "Interpretasi" : "Interpretation"}</label>
          </div>

          {/* Monetization (paid mode) */}
          <div className="mt-4 pt-4 border-t border-border space-y-4" data-testid="pdf-monetization-block">
            <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3" data-testid="free-license-block">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Asal item" : "Item origin"}</Label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {ORIGIN_OPTIONS.map((o) => (
                    <Button key={o.value || "unset"} type="button" size="sm"
                      variant={(license.origin || "") === o.value ? "default" : "outline"}
                      onClick={() => setLicense({ origin: o.value })}
                      data-testid={`license-origin-${o.value || "unset"}`}>
                      {o[lang === "id" ? "id" : "en"]}
                    </Button>
                  ))}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {lang === "id"
                    ? "Mindovia Original: item ditulis sendiri oleh Mindovia, boleh dimonetisasi. Skala terbit: item dari skala orang lain, tetap gratis. Kalau belum ditandai, daftar instrumen menebak dari baris \"Jenis:\" di catatan sumber."
                    : "Mindovia Original: items written by Mindovia, may be monetized. Published scale: items from someone else's scale, keep it free. When unset, the instrument list guesses from the \"Type:\" line of the source note."}
                </p>
              </div>
              <label className="flex items-start gap-3 text-sm">
                <Switch checked={!!license.free_only} onCheckedChange={(v) => setLicense({ free_only: v })} data-testid="free-license-toggle" />
                <span>
                  <span className="font-semibold">{lang === "id" ? "Berlisensi gratis" : "Free-use licence"}</span>
                  <span className="block text-xs text-muted-foreground">
                    {lang === "id"
                      ? `Izin pemakaian instrumen ini mensyaratkan tes dan hasilnya gratis. Mode berbayar hanya bisa dinyalakan setelah mengetik "${FREE_LICENSE_PHRASE}".`
                      : `This instrument's licence requires the test and its results to be free. Paid mode can only be turned on after typing "${FREE_LICENSE_PHRASE}".`}
                  </span>
                </span>
              </label>
              <div>
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Sumber & lisensi (internal)" : "Source & licence (internal)"}</Label>
                  {!license.note && (
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setLicense({ note: LICENSE_NOTE_TEMPLATE[lang === "id" ? "id" : "en"] })} data-testid="license-note-template">
                      {lang === "id" ? "Pakai template" : "Use template"}
                    </Button>
                  )}
                </div>
                <Textarea rows={5} value={license.note || ""} onChange={(e) => setLicense({ note: e.target.value })} data-testid="free-license-note"
                  placeholder={LICENSE_NOTE_TEMPLATE[lang === "id" ? "id" : "en"]} />
                <p className="mt-1 text-xs text-muted-foreground">
                  {lang === "id"
                    ? "Hanya terlihat di admin. Sumber untuk pengguna tetap ditulis di deskripsi, di bawah \"Tentang tes ini\"."
                    : "Admin only. The public source line still goes in the description, under \"About this test\"."}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-display text-base">{lang === "id" ? "Monetisasi Laporan" : "Report Monetization"}</div>
                <div className="text-xs text-muted-foreground max-w-2xl">
                  {lang === "id"
                    ? "Aktifkan mode berbayar agar responden harus membayar untuk membuka laporan lengkap. Pengunjung berbahasa Indonesia membayar harga rupiah lewat gateway utama (Midtrans), pengunjung berbahasa Inggris membayar harga dolar lewat PayPal. Kunci gateway diatur di Pengaturan → Gateway Pembayaran."
                    : "Enable paid mode so respondents must pay to unlock the full report. Indonesian-language visitors pay the Rupiah price through the primary gateway (Midtrans); English-language visitors pay the USD price through PayPal. Gateway keys live in Settings → Payment Gateways."}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={(pdf.mode || "free") === "paid"}
                  onCheckedChange={setPaidMode}
                  data-testid="pdf-mode-toggle"
                />
                <span className="font-semibold">{(pdf.mode || "free") === "paid" ? (lang === "id" ? "Berbayar" : "Paid") : (lang === "id" ? "Gratis" : "Free")}</span>
              </label>
            </div>
            {licenseDialogOpen && (
              <FreeLicenseDialog
                note={license.note}
                lang={lang}
                onClose={() => setLicenseDialogOpen(false)}
                onConfirm={(phrase) => {
                  setPdf({ mode: "paid" });
                  // Sent with the save; the server checks it before accepting paid mode.
                  set("license_override_phrase", phrase);
                  setLicenseDialogOpen(false);
                }}
              />
            )}

            {(pdf.mode || "free") === "paid" && (
              <div className="space-y-4" data-testid="pdf-paid-config">
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Harga (gateway utama)" : "Price (primary gateway)"}</Label>
                    <Input
                      type="number"
                      min={0}
                      step="1"
                      value={pdf.price ?? ""}
                      onChange={(e) => setPdf({ price: e.target.value === "" ? null : Number(e.target.value) })}
                      placeholder="25000"
                      className="h-10"
                      data-testid="pdf-price"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Harga USD (PayPal)" : "USD price (PayPal)"}</Label>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={pdf.price_usd ?? ""}
                      onChange={(e) => setPdf({ price_usd: e.target.value === "" ? null : Number(e.target.value) })}
                      placeholder="1.00"
                      className="h-10"
                      data-testid="pdf-price-usd"
                    />
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {lang === "id" ? "Untuk pengunjung berbahasa Inggris. Kosongkan agar semua membayar lewat gateway utama." : "For English-language visitors. Leave blank to send everyone to the primary gateway."}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Mata Uang" : "Currency"}</Label>
                    <SelectField
                      testid="pdf-currency"
                      value={(pdf.currency || "idr").toLowerCase()}
                      onChange={(v) => setPdf({ currency: v })}
                      options={[
                        { value: "idr", label: "IDR — Rupiah" },
                        { value: "usd", label: "USD — US Dollar" },
                        { value: "sgd", label: "SGD — Singapore Dollar" },
                        { value: "myr", label: "MYR — Malaysian Ringgit" },
                        { value: "eur", label: "EUR — Euro" },
                      ]}
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Masa berlaku tautan (jam)" : "Link validity (hours)"}</Label>
                    <Input
                      type="number"
                      min={1}
                      step="1"
                      value={pdf.download_link_hours ?? 24}
                      onChange={(e) => setPdf({ download_link_hours: e.target.value === "" ? 24 : Number(e.target.value) })}
                      className="h-10"
                      data-testid="pdf-link-hours"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Batas unduh (kosongkan = tak terbatas)" : "Max downloads (blank = unlimited)"}</Label>
                    <Input
                      type="number"
                      min={1}
                      step="1"
                      value={pdf.max_downloads ?? ""}
                      onChange={(e) => setPdf({ max_downloads: e.target.value === "" ? null : Number(e.target.value) })}
                      placeholder={lang === "id" ? "Tak terbatas" : "Unlimited"}
                      className="h-10"
                      data-testid="pdf-max-downloads"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Teks tombol beli" : "Purchase button text"}</Label>
                    <Input
                      value={pdf.button_text || ""}
                      onChange={(e) => setPdf({ button_text: e.target.value })}
                      placeholder={lang === "id" ? "Buka Laporan Lengkap" : "Unlock Full Report"}
                      className="h-10"
                      data-testid="pdf-button-text"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Pesan sukses" : "Success message"}</Label>
                    <Textarea
                      rows={2}
                      value={pdf.success_message || ""}
                      onChange={(e) => setPdf({ success_message: e.target.value })}
                      placeholder={lang === "id" ? "Pembayaran berhasil. Laporan lengkap Anda sudah dapat diunduh." : "Payment successful. Your full report is now available."}
                      data-testid="pdf-success-msg"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Pesan gagal" : "Failed message"}</Label>
                    <Textarea
                      rows={2}
                      value={pdf.failed_message || ""}
                      onChange={(e) => setPdf({ failed_message: e.target.value })}
                      placeholder={lang === "id" ? "Pembayaran belum berhasil. Silakan coba lagi atau hubungi kami." : "Payment did not go through. Please try again or contact us."}
                      data-testid="pdf-failed-msg"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Kontak dukungan" : "Support contact"}</Label>
                    <Input
                      value={pdf.support_contact || ""}
                      onChange={(e) => setPdf({ support_contact: e.target.value })}
                      placeholder="support@yourdomain.com"
                      className="h-10"
                      data-testid="pdf-support-contact"
                    />
                  </div>
                </div>
                <div className="text-[11px] text-muted-foreground p-3 rounded-xl bg-amber-50/60 border border-amber-200">
                  {lang === "id"
                    ? "Catatan: Jika Stripe Secret Key belum dikonfigurasi (di Pengaturan → Payment Gateway), sistem otomatis mengembalikan instrumen ini ke mode Gratis agar responden tidak masuk checkout palsu."
                    : "Note: If the Stripe Secret Key is not configured (in Settings → Payment Gateway), the system will gracefully fall back to Free mode so respondents never hit a fake checkout."}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* AI narrative */}
        <div className="p-6 rounded-2xl border border-border bg-card space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-terracotta-100 text-terracotta-700 flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
            <div>
              <div className="font-display text-lg">{lang === "id" ? "Narasi AI Reflektif" : "AI Reflective Narrative"}</div>
              <div className="text-xs text-muted-foreground max-w-2xl break-words">{lang === "id" ? "Claude Sonnet 4.5 menghasilkan narasi berbasis skor dimensi. Bahasa reflektif, non-diagnostik, otomatis menyertakan disclaimer." : "Claude Sonnet 4.5 crafts a reflective narrative from dimension scores. Non-diagnostic, disclaimer auto-added."}</div>
            </div>
            <label className="ml-auto flex items-center gap-2 text-sm"><Switch checked={!!ai.enabled} onCheckedChange={(v) => setAi({ enabled: v })} data-testid="ai-toggle" /> {lang === "id" ? "Aktifkan" : "Enable"}</label>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Prompt (ID)</Label><Textarea rows={4} value={ai.prompt_id || ""} onChange={(e) => setAi({ prompt_id: e.target.value })} placeholder={lang === "id" ? "Fokuskan narasi pada..." : "Focus the narrative on..."} data-testid="ai-prompt-id" /><PlaceholderChips onInsert={(p) => setAi({ prompt_id: (ai.prompt_id || "") + " " + p })} /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Prompt (EN)</Label><Textarea rows={4} value={ai.prompt_en || ""} onChange={(e) => setAi({ prompt_en: e.target.value })} placeholder="Focus the narrative on..." data-testid="ai-prompt-en" /><PlaceholderChips onInsert={(p) => setAi({ prompt_en: (ai.prompt_en || "") + " " + p })} /></div>
          </div>
          <div className="flex items-center gap-3">
            <Button size="sm" onClick={previewAi} disabled={previewing || !ai.enabled} className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-cream-50" data-testid="ai-preview-btn">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {previewing ? (lang === "id" ? "Menghasilkan…" : "Generating…") : (lang === "id" ? "Pratinjau Narasi" : "Preview Narrative")}
            </Button>
            {previewText && (
              <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(previewText); toast.success(lang === "id" ? "Disalin" : "Copied"); }}><CopyIcon className="w-3.5 h-3.5 mr-1.5" /> {lang === "id" ? "Salin" : "Copy"}</Button>
            )}
          </div>
          {previewText && (
            <div className="mt-2 p-4 rounded-xl border border-border bg-secondary/40 text-sm leading-relaxed whitespace-pre-wrap break-words" data-testid="ai-preview-text">{previewText}</div>
          )}
        </div>
      </ResultGroup>
    </div>
  );
}

